import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Spacer2 } from '../spacer'
import { isHTML } from '@shared/common'

export type TCardEntry = {
  displayName?: string | null
  content?: string | string[] | null
  description?: string | null
  type?: string
} & Record<string, unknown>

type FlexCardDisplayVariant = 'default' | 'body1'

export const FlexCardDisplay = ({
  cardData,
  variant = 'default',
}: {
  cardData: TCardEntry[]
  variant?: FlexCardDisplayVariant
}) => {
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const isBody1Variant = variant === 'body1'

  return (
    <Paper sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
      <Stack spacing={2}>
        {cardData.map(({ displayName, content, type, description }, index) => (
          <Box key={index}>
            <Typography
              variant={isBody1Variant ? 'body1bold' : index === 0 ? 'h5' : 'h6'}
              fontWeight={isBody1Variant ? 700 : 400}
            >
              {displayName}
            </Typography>
            {renderContent(content, type, description, 'body1')}
            {!isBody1Variant && index === 0 && <Spacer2 />}
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

function renderContent(
  content?: string | string[] | null,
  type?: string,
  description?: string | null,
  contentVariant: 'body1' = 'body1'
) {
  if ((typeof content === 'string' && !content?.trim()) || !content?.length) {
    return null
  }

  switch (type) {
    case 'URL': {
      return (
        <Link href={content as string} target="_blank">
          <Typography variant={contentVariant}>{description || content || ''}</Typography>
        </Link>
      )
    }
    default: {
      if (typeof content === 'string' && isHTML(content)) {
        return (
          <Box data-testid="flex-content">
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </Box>
        )
      } else if (typeof content === 'string') {
        return (
          <Typography variant={contentVariant} data-testid="flex-content">
            {content}
          </Typography>
        )
      }
      return (
        <Box data-testid="flex-content" mt={-2}>
          {content.map((row, idx) =>
            isHTML(row) ? (
              <div key={idx} dangerouslySetInnerHTML={{ __html: row }} />
            ) : (
              <Typography key={idx} variant={contentVariant}>
                {row}
              </Typography>
            )
          )}
        </Box>
      )
    }
  }
}
